/**
 * The decisions a toast queue makes, as pure functions.
 *
 * Separated from the reactive store next door so the interesting behaviour —
 * what coalesces, what gets evicted, how long something stays — is testable
 * without a DOM, a timer or a Svelte runtime. The store keeps the parts that
 * genuinely need those: reactivity and `setTimeout`.
 */

export type ToastKind = 'success' | 'warning' | 'error' | 'info';

export interface ToastAction {
  label: string;
  run: () => void;
}

export interface ToastInput {
  kind: ToastKind;
  message: string;
  /**
   * Identity for coalescing. A second toast with a live `key` replaces the
   * first in place instead of stacking. Defaults to `kind + message`, which
   * already collapses literal repeats.
   */
  key?: string;
  /** Overrides the per-kind default. `0` means "stay until dismissed". */
  durationMs?: number;
  action?: ToastAction;
}

export interface Toast extends ToastInput {
  id: number;
  key: string;
  durationMs: number;
}

/**
 * Errors do not expire: they usually carry the retry action, and a retry button
 * that disappears on its own while the player is reaching for it is worse than
 * no retry button at all. Everything else clears itself.
 */
export const DEFAULT_DURATION_MS: Readonly<Record<ToastKind, number>> = {
  success: 3200,
  info: 3600,
  warning: 4200,
  error: 0,
};

/** Beyond this the oldest is dropped; three already covers a third of a phone. */
export const MAX_VISIBLE = 3;

export function toastKey(input: ToastInput): string {
  return input.key ?? `${input.kind}:${input.message}`;
}

export function toastDuration(input: ToastInput): number {
  return input.durationMs ?? DEFAULT_DURATION_MS[input.kind];
}

export interface QueueResult {
  items: readonly Toast[];
  /** The toast that is now showing — new, or the existing one it replaced. */
  toast: Toast;
  /** Ids evicted to stay under `MAX_VISIBLE`, so their timers can be cleared. */
  evicted: readonly number[];
  /** True when an existing toast was updated rather than a new one added. */
  coalesced: boolean;
}

/**
 * Place `input` in `items`, coalescing by key and evicting from the front.
 *
 * Coalescing keeps the existing id on purpose: the host animates by id, so
 * reusing it means a flapping connection updates one card in place instead of
 * running an exit and an entrance for what the player reads as one message.
 */
export function enqueueToast(
  items: readonly Toast[],
  input: ToastInput,
  nextId: number,
): QueueResult {
  const key = toastKey(input);
  const durationMs = toastDuration(input);

  const existing = items.find((toast) => toast.key === key);
  if (existing) {
    const updated: Toast = { ...input, id: existing.id, key, durationMs };
    return {
      items: items.map((toast) => (toast.id === existing.id ? updated : toast)),
      toast: updated,
      evicted: [],
      coalesced: true,
    };
  }

  const toast: Toast = { ...input, id: nextId, key, durationMs };
  const overflow = Math.max(0, items.length + 1 - MAX_VISIBLE);

  return {
    items: [...items.slice(overflow), toast],
    toast,
    evicted: items.slice(0, overflow).map((dropped) => dropped.id),
    coalesced: false,
  };
}
